/**
 * @docs docs/features/website-navigation-shell
 */

/*----------------------------------
- MENU GROUP COLUMNS
----------------------------------*/

import type { LandingMarketMenuItem } from '@/client/catalogs/landing/marketMenu';
import type { LandingMenuGroup } from '@/client/catalogs/landing/menuPanels';
import type { LandingToolsMenuItem } from '@/client/catalogs/landing/toolsMenu';
import MegaMenuSection from './MegaMenuSection';
import MenuRow from './MenuRow';
import type { MenuNavigateHandler } from './navClick';

/*----------------------------------
- TYPES
----------------------------------*/

/** A Market or Tools row: either a page (`href`) or an unbuilt row (`status`), as each catalog types it. */
type MenuGroupItem = LandingMarketMenuItem | LandingToolsMenuItem;

type MenuGroupColumnsProps = {
    groups: readonly LandingMenuGroup<MenuGroupItem>[];
    /** `desktop`: one panel column per group (`.mm-sec`). `mobile`: the sheet's titled lists (`.msm-lab`, E2 = B). */
    density: 'desktop' | 'mobile';
    onNavigate: MenuNavigateHandler;
    /** The test id of each group's list, suffixed with the group id. */
    testIdPrefix: string;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * The Market and Tools groups, each under its title then its rows, from the one catalog both surfaces read: a column of
 * the desktop panel, or a titled list inside the phone sheet's section (Gaetan's decision E2 = B of 2026-11-03, the
 * sheet follows the desktop). A group's list is a `<ul>`, so a crawler and a screen reader get the count first.
 */
export default function MenuGroupColumns({ groups, density, onNavigate, testIdPrefix }: MenuGroupColumnsProps) {
    const list = (group: LandingMenuGroup<MenuGroupItem>) => (
        <ul class={density === 'desktop' ? 'mm-list' : 'msm-list'} data-testid={`${testIdPrefix}-${group.id}`}>
            {group.items.map((item) => (
                <li key={item.id}>
                    <MenuRow
                        href={item.href}
                        status={item.status}
                        label={item.label}
                        description={item.description}
                        iconName={item.iconName}
                        density={density}
                        onClick={() => item.href && onNavigate(item.id, item.href)}
                    />
                </li>
            ))}
        </ul>
    );

    return (
        <>
            {groups.map((group) =>
                density === 'desktop' ? (
                    <MegaMenuSection key={group.id} title={group.title}>{list(group)}</MegaMenuSection>
                ) : (
                    <div key={group.id}>
                        <div class="msm-lab">{group.title}</div>
                        {list(group)}
                    </div>
                ),
            )}
        </>
    );
}
